/**
 * ImageModePicker — select + visible description/example for choosing an
 * image ingestion strategy. Shared by the Create Source form, each Source
 * Card's re-index controls, and the Change Detection / Approval Pipeline
 * re-index triggers (AA-220).
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  isVisionHeavy, getResolvedImageModeOptions, DEFAULT_VISION_MODEL_LABEL,
  type ImageModeChoice,
} from './imageModeOptions';

const defaultInputStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--idx-border)', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)',
  marginTop: 3, boxSizing: 'border-box',
};

interface ImageModePickerProps {
  value: ImageModeChoice;
  onChange: (choice: ImageModeChoice) => void;
  maxVisionCalls: string;
  onMaxVisionCallsChange: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
  /** Suppress the description/example copy — just the select + cost badge (for tight layouts). */
  minimal?: boolean;
  /** Actual configured vision deployment (e.g. from AZURE_OPENAI_VISION_DEPLOYMENT,
   * surfaced via GET /agent-image-configs). Defaults to the code default so the
   * picker never shows a name that isn't at least accurate for an unconfigured
   * environment — see AA-220 follow-up ("is 4o hardcoded in the UI?"). */
  visionModel?: string;
}

const ImageModePicker: React.FC<ImageModePickerProps> = ({
  value, onChange, maxVisionCalls, onMaxVisionCallsChange, disabled, compact, minimal,
  visionModel = DEFAULT_VISION_MODEL_LABEL,
}) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const options = getResolvedImageModeOptions(visionModel);
  const selected = options.find(o => o.value === value);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <select
          value={value}
          onChange={e => onChange(e.target.value as ImageModeChoice)}
          disabled={disabled}
          style={{
            ...defaultInputStyle, width: 'auto', minWidth: compact ? 180 : 220,
            fontSize: compact ? 11 : 13, padding: compact ? '4px 8px' : undefined,
            marginTop: 0,
          }}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {selected && isVisionHeavy(value) && (
          <input
            type="number"
            min={0}
            step={1}
            value={maxVisionCalls}
            onChange={e => onMaxVisionCallsChange(e.target.value)}
            disabled={disabled}
            placeholder="cap"
            title="Max Vision API calls for this run (blank = unlimited). Caps protect against a runaway bill on a large or unfamiliar document set."
            style={{ width: 60, padding: compact ? '4px 6px' : '6px 8px', borderRadius: 6, fontSize: compact ? 11 : 13, border: '1px solid var(--idx-border-strong)', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)' }}
          />
        )}
        {selected && !minimal && (
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 500,
            color: selected.visionHeavy ? '#d97706' : '#059669',
            backgroundColor: selected.visionHeavy ? '#fffbeb' : '#ecfdf5',
          }}>
            {selected.costHint}
          </span>
        )}
      </div>
      {selected && !minimal && (
        <div style={{ fontSize: 12, color: 'var(--idx-text-muted)', lineHeight: 1.4, maxWidth: 480 }}>
          {selected.description}
          <button
            type="button"
            onClick={() => setInfoOpen(o => !o)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 6,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 11, fontWeight: 500, color: '#10b981', verticalAlign: 'middle',
            }}
          >
            {infoOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />} More info
          </button>
          {infoOpen && (
            <div style={{
              marginTop: 6, padding: '8px 10px', borderRadius: 6,
              background: 'var(--idx-bg-tertiary)', border: '1px solid var(--idx-border)',
            }}>
              <div style={{ fontStyle: 'italic', color: 'var(--idx-text-faint)', marginBottom: 6 }}>
                e.g. {selected.example}
              </div>
              {selected.goodFor.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, color: '#059669', marginBottom: 2 }}>Good for</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {selected.goodFor.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>
              )}
              {selected.notGoodFor.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 2 }}>Not a good fit for</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {selected.notGoodFor.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageModePicker;
export { ImageModePicker };
